import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, RefreshIcon,
  VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon,
  PriceIcon, EditRowIcon,
} from '../../svg/svgIcons';
import { Checkbox } from 'primereact/checkbox';
import { PanelMenu } from 'primereact/panelmenu';
import { Avatar } from 'primereact/avatar';
import UtilContext from '../../../components/UtilContext';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { resetTabulatorMask } from '../../../utils/tabulatorResetMask';
import { LocaleContext } from '../../../components/LocaleContext';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { cleanSorters, USER_MASK_UPDATE_TYPE } from '../../../utils/tabulatorProgressiveLoading';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import {
  beginChecklistTemplateDeleteBatch,
  dispatchRemoveChecklistTemplates,
  endChecklistTemplateDeleteBatch,
  ensureChecklistTemplatesLoaded,
  flushChecklistTemplatePendingInvalidation,
  refreshChecklistTemplates,
} from '../../../utils/checklistTemplatesLookup';
import {
  CHECKLIST_TEMPLATE_COLUMN_TITLES,
  CHECKLIST_TEMPLATE_SETTINGS,
  DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY,
  applyMaskToCanonicalChecklistTemplateColumns,
  buildChecklistTemplateColumns,
  buildChecklistTemplateExportFilterParams,
  buildSortedSelectOptions,
  checklistTemplateSearchFilter,
  departmentNamesFromTemplateRow,
  formatIntensityText,
  formatNotificationBefore,
  isActiveLabel,
  machineNameFromTemplateRow,
  managerNameFromTemplateRow,
  mergeChecklistTemplateColumnVisibility,
  migrateChecklistTemplateMaskRow,
  resolveChecklistTemplateFormatLabels,
  restoreChecklistTemplateHeaderFilters,
  restoreChecklistTemplateSorters,
  toSafeChecklistTemplateLayoutAttributes,
  visibleChecklistTemplateExportColumns,
} from '../../../utils/checklistTemplateSettingsList';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';

const CHECKLIST_TEMPLATE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  id: 'MachineTypeList.Column.Id',
  name: 'Common.Name',
  department_ids: 'ChecklistTemplate.Column.Department',
  users: 'ChecklistTemplate.Column.Employee',
  sending_data: 'ChecklistTemplate.Column.Intensity',
  notification_before: 'Common.Notification',
  manager_id: 'ChecklistTemplate.Column.Manager',
  manager_due_days: 'ChecklistTemplate.Column.ManagerDueDays',
  description: 'Common.Description',
  'machine.name': 'Common.Machine',
  is_active: 'ChecklistTemplate.Column.ActivityStatus',
  created_at: 'Common.Column.CreatedAt',
  updated_at: 'Common.Column.UpdatedAt',
});

const PAGE_LIST_KEY = CHECKLIST_TEMPLATE_SETTINGS.maskPage;
const TABLE_HEIGHT = 'calc(100vh - 193px)';

export default function ChecklistTemplateList() {
  const { t, locale } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const {
    setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter,
    getUserInitials, getUserColor, openChosenNotificationPreferences, getDateFormat,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [favoriteIcon] = useState(false);
  const [markUnmark, setMarkUnmarked] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY });
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isRestoringRef = useRef(false);
  const lastContainerHeightRef = useRef(0);
  const lastContainerWidthRef = useRef(0);
  const resizeRafRef = useRef(0);
  const submittedSearchValueRef = useRef('');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const dateFmt = getDateFormat?.() ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const formatLabels = useMemo(() => resolveChecklistTemplateFormatLabels(t), [t, locale]);

  const maskRow = useMemo(() => {
    const effective = getEffectiveMask(PAGE_LIST_KEY, state);
    if (effective?.type === 'user') return effective.userMask;
    if (effective?.type === 'standard') return effective.standardMask;
    return null;
  }, [state.user_mask_filters, state.standard_mask_filters]);

  const departmentOptions = useMemo(() => buildSortedSelectOptions(state.departments, {
    emptyLabel: t('Common.EmptyParen'),
    getLabel: (item) => item?.name,
    getValue: (item) => item?.id,
  }), [state.departments, t, locale]);

  const userOptions = useMemo(() => buildSortedSelectOptions(state.users, {
    emptyLabel: t('Common.EmptyParen'),
    getLabel: (item) => item?.employee?.name || item?.username || String(item?.id ?? ''),
    getValue: (item) => item?.id,
  }), [state.users, t, locale]);

  const managerOptions = useMemo(() => buildSortedSelectOptions(state.users, {
    emptyLabel: t('Common.EmptyParen'),
    getLabel: (item) => item?.employee?.name || item?.username || String(item?.id ?? ''),
    getValue: (item) => item?.id,
  }), [state.users, t, locale]);

  const machineOptions = useMemo(() => buildSortedSelectOptions(state.machines, {
    emptyLabel: t('Common.EmptyParen'),
    getLabel: (item) => item?.name,
    getValue: (item) => item?.name,
  }), [state.machines, t, locale]);

  const checklistTemplateHeaderFilters = useMemo(() => ({
    department_ids: { placeholder: t('Common.All'), options: departmentOptions },
    users: { placeholder: t('Common.All'), options: userOptions },
    manager_id: { placeholder: t('Common.All'), options: managerOptions },
    'machine.name': { placeholder: t('Common.All'), options: machineOptions },
    is_active: {
      placeholder: t('Common.All'),
      options: [
        { label: t('Common.Active'), value: '1' },
        { label: t('Common.Inactive'), value: '0' },
      ],
    },
  }), [departmentOptions, userOptions, managerOptions, machineOptions, t, locale]);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: CHECKLIST_TEMPLATE_LIST_COLUMN_I18N_KEYS,
      headerFilters: checklistTemplateHeaderFilters,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable, checklistTemplateHeaderFilters]);

  function onEditRow(checklist_template) {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistTemplateList.Tab.Edit'),
      translationKey: 'ChecklistTemplateList.Tab.Edit',
      legacyLabel: t('ChecklistTemplateList.Tab.Edit'),
      page: CHECKLIST_TEMPLATE_SETTINGS.addPage,
      props: { checklist_template, type: 'edit' },
      mask: CHECKLIST_TEMPLATE_SETTINGS.tabMask,
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  function DepartmentFormatter(props) {
    return departmentNamesFromTemplateRow(props.cell.getRow().getData()).join(', ');
  }

  function UserFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (rowData.users || []).map((user) => (
      <Avatar
        key={user?.id ?? user?.username}
        title={user?.employee?.name || user?.username}
        label={getUserInitials(user?.employee?.name || user?.username)}
        shape="circle"
        style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff', marginRight: '5px' }}
      />
    ));
  }

  function ManagerFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const label = managerNameFromTemplateRow(rowData);
    return (
      <Avatar
        title={label}
        label={getUserInitials(label)}
        shape="circle"
        style={{ backgroundColor: getUserColor(rowData?.manager?.username), color: '#ffffff', marginRight: '5px' }}
      />
    );
  }

  function IntensityFormatter(cell) {
    const row = cell.getRow?.()?.getData?.() || {};
    return formatIntensityText(row.sending_data ?? cell.getValue?.(), formatLabels);
  }

  function NotifBeforeFormatter(props) {
    return formatNotificationBefore(props.cell.getRow().getData()?.sending_data, formatLabels);
  }

  function MachineFormatter(cell) {
    return machineNameFromTemplateRow(cell.getRow()?.getData?.() || {}) || '–';
  }

  function ActiveFormatter({ cell }) {
    const rowData = cell.getRow().getData();
    const active = Number(rowData?.is_active) === 1;
    return (
      <span className={active ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
        {isActiveLabel(rowData)}
      </span>
    );
  }

  const formatters = useMemo(() => ({
    departmentFormatter: reactFormatter(<DepartmentFormatter />),
    userFormatter: reactFormatter(<UserFormatter />),
    intensityFormatter: IntensityFormatter,
    notificationFormatter: reactFormatter(<NotifBeforeFormatter />),
    managerFormatter: reactFormatter(<ManagerFormatter />),
    machineFormatter: MachineFormatter,
    activeFormatter: reactFormatter(<ActiveFormatter />),
    editRowFormatter: reactFormatter(<EditRowFormatter />),
  }), [getUserInitials, getUserColor, formatLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const canonicalColumns = useMemo(() => buildChecklistTemplateColumns({
    departmentOptions,
    userOptions,
    managerOptions,
    machineOptions,
    dateFormat: dateFmt,
    dateTimeFormat: dateTimeFmt,
    getUserColor,
    getUserInitials,
    formatLabels,
    ...formatters,
  }), [departmentOptions, userOptions, managerOptions, machineOptions, formatters, dateFmt, dateTimeFmt, getUserColor, getUserInitials, formatLabels]);

  const maskLayoutKey = useMemo(() => {
    if (!maskRow) return '';
    const migrated = migrateChecklistTemplateMaskRow(maskRow);
    return JSON.stringify({
      columns: mergeChecklistTemplateColumnVisibility(migrated.columns),
      attributes: toSafeChecklistTemplateLayoutAttributes(migrated.attributes),
    });
  }, [maskRow]);

  const appliedMask = useMemo(
    () => applyMaskToCanonicalChecklistTemplateColumns(canonicalColumns, maskRow),
    [canonicalColumns, maskLayoutKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const columns = appliedMask.columns;

  useEffect(() => {
    setColumnsVisible(appliedMask.visibility);
  }, [appliedMask.visibility]);

  const searchTable = () => {
    const table = getTable();
    if (!table) return;
    const needle = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = needle;
    if (needle === '') table.clearFilter();
    else table.setFilter((data) => checklistTemplateSearchFilter(data, needle, formatLabels));
    setVisibleSearch(false);
  };

  useEffect(() => {
    ensureChecklistTemplatesLoaded({ dispatch, state });
    // loaded is the bootstrap gate; ensure reads current state internally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.checklist_templates_loaded]);

  useEffect(() => {
    flushChecklistTemplatePendingInvalidation({ dispatch, state });
    // pending invalidation flushes only when this list tab becomes visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.selectedTab, state.panels]);

  const reloadChecklistTemplates = useCallback(() => {
    setDialogText(t('Common.Updating'));
    setVisible(true);
    refreshChecklistTemplates({ dispatch })
      .then(() => setVisible(false))
      .catch(() => setVisible(false));
  }, [dispatch, setDialogText, setVisible]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const columnNodes = table?.getColumns?.() ?? columns;
    return visibleChecklistTemplateExportColumns(columnNodes, columnsVisible);
  }, [getTable, columns, columnsVisible]);

  const buildChecklistTemplateExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...buildChecklistTemplateExportFilterParams(table?.getHeaderFilters?.() ?? []),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable]);

  const runChecklistTemplatePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildChecklistTemplateExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/checklist_templates/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildChecklistTemplateExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportChecklistTemplateXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildChecklistTemplateExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.CHECKLIST_TEMPLATES));
      const { blob, filename } = await downloadBlobGet(`/api/checklist_templates/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.PreparingExport'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildChecklistTemplateExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const accept = () => {
    const rows_to_delete = getTable()?.getSelectedData?.() || [];
    setDialogText(t('Common.Deleting'));
    setVisible(true);
    const deletePromises = rows_to_delete.map((element) =>
      deleteRequest(`/api/checklist_templates/${element.id}`).then(() => element.id)
    );
    beginChecklistTemplateDeleteBatch();
    Promise.allSettled(deletePromises).then((results) => {
      setVisible(false);
      const successfulIds = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value)
        .filter((id) => id != null);
      const failCount = results.filter((r) => r.status === 'rejected').length;
      if (successfulIds.length > 0) {
        dispatchRemoveChecklistTemplates(dispatch, successfulIds);
      }
      return endChecklistTemplateDeleteBatch({ dispatch, state }).finally(() => {
        if (successfulIds.length > 0 && failCount === 0) {
          toast?.current?.show({
            severity: 'info',
            summary: t('Common.Confirmed'),
            life: 3000,
            detail: successfulIds.length === 1 ? t('Common.Toast.RecordDeletedDetail') : t('Common.Toast.RecordsDeletedDetail').replace('{{count}}', String(successfulIds.length)),
          });
        } else if (successfulIds.length > 0 && failCount > 0) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Toast.PartialDeletedSummary'), detail: t('Common.Toast.PartialDeletedDetail').replace('{{success}}', String(successfulIds.length)).replace('{{fail}}', String(failCount)), life: 3000 });
        } else if (failCount > 0) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.DeleteFailedDetail'), life: 3000 });
        }
      });
    });
  };

  const reject = () => {
    toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.RequestCancelled'), life: 3000 });
  };

  const confirm = () => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept,
      reject,
    });
  };

  const onSelectRow = (data) => {
    if (!data || data.length === 0) {
      setEditIcon(true);
      setCopyIcon(true);
      setDeleteIcon(true);
    } else {
      setEditIcon(false);
      setCopyIcon(false);
      setDeleteIcon(false);
    }
  };

  function addNewDepartment() {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistTemplateList.Tab.New'),
      translationKey: 'ChecklistTemplateList.Tab.New',
      legacyLabel: t('ChecklistTemplateList.Tab.New'),
      page: CHECKLIST_TEMPLATE_SETTINGS.addPage,
      props: { checklist_template: null, type: 'add' },
      mask: CHECKLIST_TEMPLATE_SETTINGS.tabMask,
    });
  }

  function copyDepartment() {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistTemplateList.Tab.New'),
      translationKey: 'ChecklistTemplateList.Tab.New',
      legacyLabel: t('ChecklistTemplateList.Tab.New'),
      page: CHECKLIST_TEMPLATE_SETTINGS.addPage,
      props: { checklist_template: getTable()?.getSelectedData?.()?.[0], type: 'add' },
      mask: CHECKLIST_TEMPLATE_SETTINGS.tabMask,
    });
  }

  function editDepartment() {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistTemplateList.Tab.Edit'),
      translationKey: 'ChecklistTemplateList.Tab.Edit',
      legacyLabel: t('ChecklistTemplateList.Tab.Edit'),
      page: CHECKLIST_TEMPLATE_SETTINGS.addPage,
      props: { checklist_template: getTable()?.getSelectedData?.()?.[0], type: 'edit' },
      mask: CHECKLIST_TEMPLATE_SETTINGS.tabMask,
    });
  }

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const persistSafeLayout = () => {
    if (isRestoringRef.current) return;
    const table = getTable();
    if (!table?.getColumnLayout) return;
    const safe = toSafeChecklistTemplateLayoutAttributes(table.getColumnLayout());
    if (Array.isArray(safe) && safe.length > 0) {
      setUserMaskFilter(PAGE_LIST_KEY, USER_MASK_UPDATE_TYPE.COLUMN_LAYOUT, null, null, safe);
    }
  };

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        {
          key: '0_1',
          label: t('Common.SetFilter'),
          icon: 'pi pi-filter',
          command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); },
          template: itemRenderer,
        },
        {
          key: '0_2',
          label: t('Common.ClearFilter'),
          icon: 'pi pi-filter-slash',
          command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); },
          template: itemRenderer,
        },
        {
          key: '0_3',
          label: t('ServiceTask.SetSort'),
          icon: 'pi pi-sort-alt',
          command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, getTable()?.getSorters?.() ?? []); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => { setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_5b',
          label: t('Common.ResetUserMask'),
          icon: 'pi pi-refresh',
          command: () => { resetMask(); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_5',
          label: t('Common.SetMask'),
          icon: 'pi pi-server',
          command: () => { setMaskVisible(true); closeMenu(); },
          template: itemRenderer,
        },
        {
          key: '0_6',
          label: t('Common.Notifications'),
          icon: 'pi pi-envelope',
          command: () => { openChosenNotificationPreferences(PAGE_LIST_KEY); closeMenu(); },
          template: itemRenderer,
        },
      ],
    },
  ];

  function changeMarkings(value) {
    const next = { ...columnsVisible };
    Object.keys(DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY).forEach((field) => {
      next[field] = value;
      if (value) getTable()?.showColumn?.(field);
      else getTable()?.hideColumn?.(field);
    });
    setColumnsVisible(next);
    setTimeout(() => getTable()?.redraw?.(true), 0);
  }

  function setMask(item, value) {
    const next = { ...columnsVisible, [item]: value };
    Object.keys(next).forEach((field) => {
      if (next[field] === false) getTable()?.hideColumn?.(field);
      else getTable()?.showColumn?.(field);
    });
    setColumnsVisible(next);
    setTimeout(() => getTable()?.redraw?.(true), 0);
  }

  const resetMask = () => resetTabulatorMask({
    page: PAGE_LIST_KEY,
    defaultColumnsVisible: DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY,
    ref,
    columns,
    setColumnsVisible,
    getFallbackMask: () => {
      const standard = (state.standard_mask_filters || []).find((mask) => mask.page === PAGE_LIST_KEY);
      if (!standard) return null;
      const migrated = migrateChecklistTemplateMaskRow(standard);
      return {
        columns: mergeChecklistTemplateColumnVisibility(migrated.columns),
        attributes: [],
      };
    },
    onAfterReset: () => {
      const standard = (state.standard_mask_filters || []).find((mask) => mask.page === PAGE_LIST_KEY);
      const next = applyMaskToCanonicalChecklistTemplateColumns(canonicalColumns, standard || null);
      setColumnsVisible(next.visibility);
    },
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    lastContainerHeightRef.current = el.clientHeight;
    lastContainerWidthRef.current = el.clientWidth;
    const observer = new ResizeObserver(() => {
      const height = el.clientHeight;
      const width = el.clientWidth;
      if (height > 0 && width > 0) {
        const wasHidden = lastContainerHeightRef.current <= 0 || lastContainerWidthRef.current <= 0;
        lastContainerHeightRef.current = height;
        lastContainerWidthRef.current = width;
        if (!wasHidden) return;
        if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = requestAnimationFrame(() => {
          resizeRafRef.current = 0;
          getTable()?.redraw?.(true);
        });
      } else {
        lastContainerHeightRef.current = 0;
        lastContainerWidthRef.current = 0;
      }
    });
    observer.observe(el);
    return () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = 0;
      }
      observer.disconnect();
    };
  }, [getTable]);

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" onClick={addNewDepartment}><AddIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editDepartment}><EditIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copyDepartment}><CopyIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" onClick={confirm} disabled={deleteIcon}><DeleteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search"><button type="button" onClick={() => setVisibleSearch(true)}><SearchIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" onClick={exportChecklistTemplateXlsx}><ExportIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button
              type="button"
              title={t('Common.Refresh')}
              onClick={reloadChecklistTemplates}
            >
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first"><button type="button" onClick={scrollToFirst}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back"><button type="button" onClick={scrollUp}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward"><button type="button" onClick={scrollDown}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last"><button type="button" onClick={scrollToLast}><ForwardToLastIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button type="button" className="disabled:opacity-25" disabled={favoriteIcon} onClick={() => openFavoriteDialog(t('ChecklistTemplateList.FavoriteTitle'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="price"><button type="button" onClick={() => setFastView(PAGE_LIST_KEY)}><PriceIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="flex flex-col overflow-hidden min-h-[calc(100vh-177px)]">
        <div ref={tableContainerRef} className="inline-block w-full py-2">
          <ReactTabulator
            data={state.checklist_templates}
            className="h-[calc(100vh_-_193px)]"
            onRef={(r) => { ref.current = r?.current; }}
            columns={columns}
            events={{
              rowSelectionChanged: onSelectRow,
              tableBuilt: () => {
                const table = getTable();
                if (!table) return;
                isRestoringRef.current = true;
                restoreChecklistTemplateHeaderFilters(table, maskRow, columns);
                restoreChecklistTemplateSorters(table, maskRow, columns);
                isRestoringRef.current = false;
              },
              columnMoved: persistSafeLayout,
              columnResized: persistSafeLayout,
            }}
            options={{
              layout: 'fitColumns',
              renderHorizontal: 'virtual',
              movableColumns: true,
            printAsHtml: false,
              autoResize: true,
              headerSortTristate: true,
              filterMode: 'local',
              sortMode: 'local',
              height: TABLE_HEIGHT,
            }}
          />
        </div>
        <PrintOptionsDialog
          visible={printChoiceVisible}
          onHide={() => setPrintChoiceVisible(false)}
          orientation={printOrientationChoice}
          onOrientationChange={setPrintOrientationChoice}
          layout={printLayoutChoice}
          onLayoutChange={setPrintLayoutChoice}
          onConfirm={runChecklistTemplatePrint}
        />
        <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
          <div className="flex flex-columns">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
            <button type="button" onClick={searchTable} className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"><SearchIcon /></button>
          </div>
        </Dialog>
        <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
          <div className="flex flex-col">
            <button type="button" onClick={() => { changeMarkings(!markUnmark); setMarkUnmarked(!markUnmark); }} className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500">
              {markUnmark ? t('Common.DeselectAllUpper') : t('Common.SelectAllUpper')}
            </button>
            {Object.keys(DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY).map((field) => (
              <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <Checkbox onChange={(e) => setMask(field, e.checked)} checked={columnsVisible[field]} />
                <span className="ml-2">{CHECKLIST_TEMPLATE_COLUMN_TITLES[field] || field}</span>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setUserMaskFilter(PAGE_LIST_KEY, 'set_columns', columnsVisible, null, null);
                setMaskVisible(false);
              }}
              className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"
            >
              ULOŽIŤ
            </button>
          </div>
        </Dialog>
      </div>
    </>
  );
}
